import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      alpha: number;
      color: string;
    }> = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const colors = ['#00D9FF', '#8B5CF6', '#22D3EE', '#EC4899'];
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 3 + 1,
        alpha: Math.random() * 0.6 + 0.2,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = '#00D9FF';
            ctx.globalAlpha = (1 - dist / 150) * 0.15;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden bg-dark-900"
    >
      <div className="absolute inset-0 bg-gradient-radial from-neon-purple/10 via-dark-900 to-dark-900" />
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-transparent to-dark-900" />
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />

      <div className="relative z-10 w-full px-4 md:px-6 lg:px-12 py-32 md:py-40 flex flex-col items-center text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-dark-400/60 border border-neon-blue/20 mb-8 animate-float">
          <span className="w-2 h-2 rounded-full bg-neon-blue animate-pulse" />
          <span className="text-xs md:text-sm text-gray-300 font-medium">{t('hero.badge')}</span>
        </div>

        <h1 className="font-display font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white leading-tight max-w-5xl">
          {t('hero.title1')}
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-blue to-neon-purple">
            {t('hero.title2')}
          </span>{' '}
          {t('hero.withUs')}
        </h1>

        <p className="mt-6 md:mt-8 text-base md:text-lg lg:text-xl text-gray-400 max-w-2xl leading-relaxed">
          {t('hero.subtitle')}
        </p>

        <div className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-4 items-center">
          <button
            onClick={() => scrollTo('contact')}
            className="px-8 py-4 bg-neon-blue text-dark-900 font-semibold rounded-full hover:bg-neon-cyan transition-all duration-300 hover:shadow-[0_0_30px_#00D9FF40] whitespace-nowrap cursor-pointer"
          >
            {t('hero.startProject')}
          </button>
          <button
            onClick={() => scrollTo('portfolio')}
            className="px-8 py-4 border border-white/20 text-white font-semibold rounded-full hover:border-neon-blue/50 hover:text-neon-blue transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            {t('hero.viewPortfolio')}
            <i className="ri-arrow-right-line" />
          </button>
        </div>

        <div className="mt-16 md:mt-24 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8 md:gap-12 text-gray-500">
          <div className="flex items-center gap-2">
            <span className="text-2xl md:text-3xl font-display font-bold text-white">50+</span>
            <span className="text-xs md:text-sm">{t('hero.projects')}</span>
          </div>
          <div className="hidden sm:block w-px h-8 bg-white/10" />
          <div className="flex items-center gap-2">
            <span className="text-2xl md:text-3xl font-display font-bold text-white">2+</span>
            <span className="text-xs md:text-sm">{t('hero.years')}</span>
          </div>
          <div className="hidden sm:block w-px h-8 bg-white/10" />
          <div className="flex items-center gap-2">
            <span className="text-2xl md:text-3xl font-display font-bold text-white">99%</span>
            <span className="text-xs md:text-sm">{t('hero.satisfaction')}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
